import { useState, useEffect, useCallback } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  PlusCircle,
  Calendar,
  MessageSquare,
  Store,
  Crown,
  LogOut,
  Eye,
  Pencil,
  Copy,
  MoreVertical,
  BadgeCheck,
  TrendingUp,
  Info,
  X,
} from 'lucide-react'
import { STRIPE, CATS } from '@/data/mockData'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { api } from '@/lib/api'
import { toast } from '@/components/ui/Toast'
import type { Database } from '@parchala/shared'

// ─── Tipos Supabase ───────────────────────────────────────────────────────────
type BizRow = Database['public']['Tables']['businesses']['Row']
type PostRow = Pick<
  Database['public']['Tables']['posts']['Row'],
  'id' | 'title' | 'type' | 'is_active' | 'interested_count' | 'view_count'
>

// Hook para cargar datos reales del negocio logueado
function useBizData() {
  const { user } = useAuth()
  const [biz, setBiz] = useState<BizRow | null>(null)
  const [posts, setPosts] = useState<PostRow[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!user || !supabase) {
      setLoading(false)
      return
    }
    setLoading(true)

    // Cargar UN negocio del usuario
    const { data: bizList } = await supabase
      .from('businesses')
      .select('*')
      .eq('owner_id', user.id)
      .limit(1)

    const theBiz = bizList?.[0] ?? null
    setBiz(theBiz)

    if (theBiz) {
      const { data: postsData } = await supabase
        .from('posts')
        .select('id,title,type,is_active,interested_count,view_count')
        .eq('business_id', theBiz.id)
        .order('created_at', { ascending: false })
      setPosts(postsData ?? [])
    }

    setLoading(false)
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load()
  }, [load])

  return { biz, posts, loading, reload: load }
}

const PLANS = [
  {
    key: 'free' as const,
    name: 'Gratis',
    price: '$0/mes',
    color: '#6D7471',
    features: ['Perfil básico', 'Hasta 3 publicaciones/mes', 'Estadísticas básicas'],
  },
  {
    key: 'professional' as const,
    name: 'Profesional',
    price: '$29/mes',
    color: '#0F5C5C',
    features: [
      'Publicaciones ilimitadas',
      'Promociones exprés',
      'Historias 24h',
      'Programación',
      'Estadísticas avanzadas',
      'Soporte prioritario',
    ],
  },
  {
    key: 'featured' as const,
    name: 'Destacado',
    price: '$79/mes',
    color: '#D9785B',
    features: [
      'Todo lo de Profesional',
      'Posición destacada',
      'Publicaciones patrocinadas',
      'Segmentación por zona',
      'Manager dedicado',
    ],
  },
]

const COMMENTS = [
  {
    user: 'Andrea M.',
    av: '#D9785B',
    text: '¿Hasta qué hora aplica la entrada gratis?',
    time: 'hace 8 min',
  },
  {
    user: 'Carlos R.',
    av: '#0F5C5C',
    text: 'El ambiente estuvo increíble el viernes pasado',
    time: 'hace 22 min',
  },
  { user: 'Valeria P.', av: '#D3A34A', text: '¿Aceptan reservas de grupo?', time: 'hace 1 h' },
]

const C = {
  teal: '#0F5C5C',
  tealDk: '#153D3D',
  terra: '#D9785B',
  amber: '#D3A34A',
  grayBg: '#F1F3F2',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  success: '#3E8B63',
  error: '#C85C5C',
  live: '#D85F5F',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

// ─── Shell del negocio ────────────────────────────────────────────────────────
function BizShell({ children, biz }: { children: React.ReactNode; biz: BizRow }) {
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const navItems = [
    { to: '/dashboard', icon: <LayoutDashboard size={20} />, label: 'Inicio' },
    { to: '/dashboard/create', icon: <PlusCircle size={20} />, label: 'Publicar' },
    { to: '/dashboard/activities', icon: <Calendar size={20} />, label: 'Actividades' },
    { to: '/dashboard/community', icon: <MessageSquare size={20} />, label: 'Comunidad', badge: 5 },
    { to: '/dashboard/profile', icon: <Store size={20} />, label: 'Perfil' },
    { to: '/dashboard/subscription', icon: <Crown size={20} />, label: 'Suscripción' },
  ]

  return (
    <div className="biz-shell-wrap">
      {/* Overlay drawer */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 99, background: 'rgba(0,0,0,.45)' }}
        />
      )}
      {/* Topbar */}
      <header className="biz-topbar">
        <button
          className="biz-hamburger"
          onClick={() => setDrawerOpen((v) => !v)}
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: '#fff',
            flexShrink: 0,
          }}
        >
          ☰
        </button>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50% 50% 50% 4px',
              background: C.teal,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: 16 }}>⚡</span>
          </div>
          <span
            style={{
              fontFamily: FH,
              fontWeight: 800,
              fontSize: 20,
              color: C.teal,
              letterSpacing: '-0.02em',
            }}
          >
            Párchala
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginLeft: 4 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: 99,
              background: C.success,
              animation: 'pxPulse 2s infinite',
            }}
          />
          <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 15 }}>{biz?.name ?? '—'}</span>
          {biz?.verified && <BadgeCheck size={16} color={C.teal} />}
          <span style={{ fontSize: 12, color: C.ink2 }}>
            {biz?.category} · {biz?.zone}
          </span>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '9px 14px',
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: '#fff',
              fontFamily: FB,
              fontWeight: 600,
              fontSize: 13.5,
              cursor: 'pointer',
              color: C.ink2,
            }}
          >
            <Eye size={16} /> Ver como cliente
          </button>
          <button
            onClick={signOut}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '9px 14px',
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: '#fff',
              fontFamily: FB,
              fontWeight: 600,
              fontSize: 13.5,
              cursor: 'pointer',
              color: C.ink2,
            }}
          >
            <LogOut size={16} /> Salir
          </button>
        </div>
      </header>

      <div className="biz-body">
        {/* Sidebar */}
        <nav
          className={`biz-sidebar${drawerOpen ? ' biz-drawer' : ''}`}
          style={{ background: '#fff', borderRight: `1px solid ${C.border}`, padding: '14px 10px' }}
        >
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              onClick={() => setDrawerOpen(false)}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 12,
                textDecoration: 'none',
                marginBottom: 3,
                background: isActive ? `${C.teal}14` : 'transparent',
                color: isActive ? C.teal : C.ink2,
                fontFamily: FB,
                fontWeight: isActive ? 700 : 500,
                fontSize: 14.5,
              })}
            >
              {item.icon}
              <span className="biz-nav-label">{item.label}</span>
              {item.badge && (
                <span
                  className="biz-nav-badge"
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#fff',
                    background: C.terra,
                    borderRadius: 99,
                    padding: '2px 7px',
                  }}
                >
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
          <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
            <button
              onClick={signOut}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 12,
                border: 'none',
                background: 'transparent',
                color: C.ink2,
                fontFamily: FB,
                fontWeight: 500,
                fontSize: 14.5,
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <LogOut size={20} color={C.ink2} /> Cerrar sesión
            </button>
          </div>
        </nav>

        {/* Contenido */}
        <main className="biz-main">{children}</main>
      </div>
    </div>
  )
}

// ─── Tarjeta de métrica ────────────────────────────────────────────────────────
function MetricCard({
  icon,
  val,
  label,
  delta,
  color = C.teal,
}: {
  icon: string
  val: string
  label: string
  delta?: string
  color?: string
}) {
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 8,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          {icon}
        </div>
        {delta && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.success,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <TrendingUp size={12} /> {delta}
          </span>
        )}
      </div>
      <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 20, marginBottom: 1 }}>{val}</div>
      <div style={{ fontSize: 12.5, color: C.ink2 }}>{label}</div>
    </div>
  )
}

// ─── INICIO ───────────────────────────────────────────────────────────────────
function BizHome({ biz, posts }: { biz: BizRow; posts: PostRow[] }) {
  const navigate = useNavigate()
  const hr = new Date().getHours()
  const greeting = hr < 12 ? 'Buenos días' : hr < 19 ? 'Buenas tardes' : 'Buenas noches'
  const activePosts = posts.filter((p) => p.is_active)
  const totalInterested = posts.reduce((s, p) => s + p.interested_count, 0)
  const totalViews = posts.reduce((s, p) => s + p.view_count, 0)

  const quickActions = [
    { emoji: '🏷️', label: 'Promoción exprés', color: C.terra },
    { emoji: '📅', label: 'Publicar evento', color: C.teal },
    { emoji: '📡', label: 'Actualización en vivo', color: C.live },
    { emoji: '📸', label: 'Crear historia', color: C.amber },
    { emoji: '🌡️', label: 'Actualizar ambiente', color: C.success },
    { emoji: '🏪', label: 'Anunciar apertura', color: C.tealDk },
  ]

  return (
    <div className="page-biz">
      {/* Saludo */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 22, marginBottom: 3 }}>
          {greeting} 👋
        </div>
        <div style={{ fontSize: 15, color: C.ink2 }}>
          {activePosts.length > 0
            ? `Tienes ${activePosts.length} publicaciones activas en este momento.`
            : 'Aún no tienes publicaciones activas. ¡Crea tu primera!'}
        </div>
      </div>

      {/* Métricas */}
      <div className="metrics-4" style={{ marginBottom: 10 }}>
        <MetricCard
          icon="👥"
          val={biz.follower_count.toLocaleString()}
          label="Seguidores"
          color={C.teal}
        />
        <MetricCard
          icon="❤️"
          val={totalInterested.toLocaleString()}
          label="Interesados totales"
          color={C.terra}
        />
        <MetricCard
          icon="👁️"
          val={totalViews.toLocaleString()}
          label="Visitas totales"
          color={C.amber}
        />
        <MetricCard
          icon="📋"
          val={posts.length.toLocaleString()}
          label="Publicaciones"
          color={C.success}
        />
      </div>

      {/* Acciones rápidas */}
      <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
        Acciones rápidas
      </div>
      <div className="quick-biz" style={{ marginBottom: 16 }}>
        {quickActions.map((a) => (
          <button
            key={a.label}
            onClick={() => navigate('/dashboard/create')}
            style={{
              textAlign: 'left',
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              padding: '12px 10px',
              cursor: 'pointer',
              fontFamily: FB,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: `${a.color}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 8,
                fontSize: 18,
              }}
            >
              {a.emoji}
            </div>
            <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 12 }}>{a.label}</div>
          </button>
        ))}
      </div>

      {/* Actividades activas */}
      <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 17, marginBottom: 14 }}>
        Actividades activas ({activePosts.length})
      </div>

      {activePosts.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '32px',
            background: '#fff',
            borderRadius: 16,
            border: `1px solid ${C.border}`,
            color: C.ink2,
          }}
        >
          No tienes publicaciones activas todavía.
          <br />
          <button
            onClick={() => navigate('/dashboard/create')}
            style={{
              marginTop: 12,
              padding: '10px 18px',
              borderRadius: 11,
              border: 'none',
              background: C.teal,
              color: '#fff',
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            + Crear publicación
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {activePosts.slice(0, 5).map((p) => {
            const col = p.type === 'live' ? C.live : p.type === 'promo' ? C.terra : C.teal
            const lbl =
              p.type === 'live'
                ? '📡 En vivo'
                : p.type === 'promo'
                  ? '🏷️ Promoción'
                  : p.type === 'event'
                    ? '📅 Evento'
                    : p.type === 'free'
                      ? '🎁 Gratis'
                      : p.type
            return (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  gap: 14,
                  alignItems: 'center',
                  padding: 14,
                  background: '#fff',
                  border: `1px solid ${C.border}`,
                  borderRadius: 14,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 12,
                    background: STRIPE(col, C.tealDk),
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                  }}
                >
                  {p.type === 'live'
                    ? '📡'
                    : p.type === 'promo'
                      ? '🏷️'
                      : p.type === 'event'
                        ? '📅'
                        : p.type === 'experience'
                          ? '✨'
                          : '📋'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: col, marginBottom: 2 }}>
                    {lbl}
                  </div>
                  <div
                    style={{
                      fontFamily: FH,
                      fontWeight: 700,
                      fontSize: 14,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.title}
                  </div>
                  <div
                    style={{ display: 'flex', gap: 12, fontSize: 12, color: C.ink2, marginTop: 3 }}
                  >
                    <span>❤️ {p.interested_count} interesados</span>
                    <span>👁️ {p.view_count} visitas</span>
                  </div>
                </div>
                <button
                  onClick={() => toast.info('Editando: ' + p.title)}
                  style={{
                    border: `1px solid ${C.border}`,
                    background: '#fff',
                    borderRadius: 9,
                    width: 34,
                    height: 34,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Pencil size={15} />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── PUBLICAR ─────────────────────────────────────────────────────────────────
type ActType = 'promo' | 'event' | 'live'

const ACT_META: Record<
  ActType,
  { title: string; badge: string; color: string; desc: string; emoji: string }
> = {
  promo: {
    title: 'Oferta exprés',
    badge: 'PROMOCIÓN',
    color: C.terra,
    desc: '2x1, happy hour, descuento — con vencimiento',
    emoji: '🏷️',
  },
  event: {
    title: 'Actividad programada',
    badge: 'EVENTO',
    color: C.teal,
    desc: 'Una fecha y hora específicas',
    emoji: '📅',
  },
  live: {
    title: 'En vivo',
    badge: 'EN VIVO',
    color: C.live,
    desc: 'Está pasando ahora mismo',
    emoji: '📡',
  },
}

function BizCreate({ biz, onCreated }: { biz: BizRow; onCreated?: () => void }) {
  const navigate = useNavigate()
  const [kind, setKind] = useState<'permanent' | 'activity' | null>(null)
  const [actType, setActType] = useState<ActType | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [priceInfo, setPriceInfo] = useState('')
  const [conditions, setConditions] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const resetForm = () => {
    setTitle('')
    setDescription('')
    setCategory('')
    setDate('')
    setStartTime('')
    setEndsAt('')
    setPriceInfo('')
    setConditions('')
  }
  const backToStart = () => {
    setKind(null)
    setActType(null)
    resetForm()
  }

  const isPermanent = kind === 'permanent'
  const badge = isPermanent ? 'PUBLICACIÓN' : actType ? ACT_META[actType].badge : ''
  const headerTitle = isPermanent ? 'Publicación' : actType ? ACT_META[actType].title : ''

  const publish = async () => {
    const dbType = isPermanent ? 'social' : actType
    if (!dbType) return
    if (title.trim().length < 3) {
      toast.error('El título debe tener al menos 3 caracteres')
      return
    }
    setSubmitting(true)
    try {
      const payload: Record<string, unknown> = {
        businessId: biz.id,
        type: dbType,
        isPermanent,
        title: title.trim(),
        description: description.trim() || undefined,
        category: category || undefined,
        priceInfo: priceInfo.trim() || undefined,
        conditions: conditions.trim() || undefined,
      }
      if (actType === 'event') {
        if (date) payload.scheduledDate = date
        if (startTime.trim()) payload.startTime = startTime.trim()
      }
      if (actType === 'promo' && endsAt) payload.endsAt = new Date(endsAt).toISOString()

      await api.post('/api/posts', payload)
      toast.success(isPermanent ? 'Publicación creada ✓' : 'Actividad publicada ✓')
      backToStart()
      onCreated?.()
      navigate('/dashboard/activities')
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  const field = (label: string, el: React.ReactNode) => (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{ fontFamily: FH, fontWeight: 700, fontSize: 13, marginBottom: 7, color: C.ink2 }}
      >
        {label}
      </div>
      {el}
    </div>
  )
  const ctrlInput = (
    value: string,
    onChange: (v: string) => void,
    placeholder: string,
    kind = 'text'
  ) => (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={kind}
      style={{
        width: '100%',
        border: `1px solid ${C.border}`,
        borderRadius: 11,
        padding: '12px 14px',
        fontFamily: FB,
        fontSize: 14,
        outline: 'none',
      }}
    />
  )

  const backBtnStyle: React.CSSProperties = {
    width: 38,
    height: 38,
    borderRadius: 11,
    border: `1px solid ${C.border}`,
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  }

  const optionCard = (
    emoji: string,
    label: string,
    desc: string,
    color: string,
    onClick: () => void
  ) => (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: 22,
        cursor: 'pointer',
        fontFamily: FB,
        transition: 'all .15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.1)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.border
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 14,
          fontSize: 24,
        }}
      >
        {emoji}
      </div>
      <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: C.ink2, lineHeight: 1.4 }}>{desc}</div>
    </button>
  )

  // Paso 1 — ¿Publicación permanente o actividad temporal?
  if (!kind) {
    return (
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '28px 28px 60px' }}>
        <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26, marginBottom: 6 }}>
          Crear publicación
        </div>
        <div style={{ fontSize: 15, color: C.ink2, marginBottom: 24 }}>¿Qué quieres publicar?</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
          {optionCard(
            '📌',
            'Publicación',
            'Queda en tu perfil de forma permanente. La ven quienes te siguen y quienes entran a tu perfil.',
            C.teal,
            () => setKind('permanent')
          )}
          {optionCard(
            '⚡',
            'Actividad',
            'Promo, evento o algo en vivo. Sale en Inicio, Ahora y el mapa mientras está vigente, y luego queda en tu perfil.',
            C.terra,
            () => setKind('activity')
          )}
        </div>
      </div>
    )
  }

  // Paso 2 — Si es actividad, ¿de qué tipo?
  if (kind === 'activity' && !actType) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 28px 60px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          <button onClick={backToStart} style={backBtnStyle}>
            <X size={18} />
          </button>
          <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 24 }}>Actividad</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
          {(['event', 'promo', 'live'] as ActType[]).map((a) => (
            <div key={a}>
              {optionCard(
                ACT_META[a].emoji,
                ACT_META[a].title,
                ACT_META[a].desc,
                ACT_META[a].color,
                () => setActType(a)
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
        <button
          onClick={() => (kind === 'activity' ? setActType(null) : backToStart())}
          style={backBtnStyle}
        >
          <X size={18} />
        </button>
        <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 24 }}>{headerTitle}</div>
      </div>

      <div className="biz-two-col">
        {/* Formulario */}
        <div
          style={{
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: 24,
          }}
        >
          {field(
            'Imagen / video (próximamente)',
            <div
              style={{
                height: 160,
                borderRadius: 13,
                border: `1px dashed ${C.border}`,
                background: C.grayBg,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                color: C.ink2,
                fontSize: 28,
              }}
            >
              📷
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                La subida de imágenes llega pronto
              </span>
            </div>
          )}
          {field('Título *', ctrlInput(title, setTitle, 'Ej: 2x1 en cocteles de la casa'))}
          {field(
            'Descripción',
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe la actividad, las condiciones, el ambiente…"
              rows={3}
              style={{
                width: '100%',
                border: `1px solid ${C.border}`,
                borderRadius: 11,
                padding: '12px 14px',
                fontFamily: FB,
                fontSize: 14,
                outline: 'none',
                resize: 'none',
              }}
            />
          )}
          {field(
            'Categoría',
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                width: '100%',
                border: `1px solid ${C.border}`,
                borderRadius: 11,
                padding: '12px 14px',
                fontFamily: FB,
                fontSize: 14,
                outline: 'none',
                background: '#fff',
              }}
            >
              <option value="">Sin categoría</option>
              {CATS.map((c) => (
                <option key={c.k} value={c.k}>
                  {c.emoji} {c.label}
                </option>
              ))}
            </select>
          )}

          {/* Actividad programada → fecha y hora */}
          {actType === 'event' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {field('Fecha', ctrlInput(date, setDate, '', 'date'))}
              {field('Hora de inicio', ctrlInput(startTime, setStartTime, '7:00 p. m.'))}
            </div>
          )}

          {/* Oferta exprés → cuándo vence (sale del feed/mapa al pasar) */}
          {actType === 'promo' &&
            field(
              'Vence (sale del feed al pasar)',
              ctrlInput(endsAt, setEndsAt, '', 'datetime-local')
            )}

          {/* Precio y condiciones — solo para actividades */}
          {kind === 'activity' && (
            <>
              {field(
                'Precio (informativo)',
                ctrlInput(priceInfo, setPriceInfo, 'Ej: $12 · Gratis')
              )}
              {field(
                'Condiciones',
                ctrlInput(conditions, setConditions, 'Aplica en productos seleccionados')
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              onClick={() => toast.info('Los borradores estarán disponibles pronto')}
              style={{
                flex: 1,
                padding: 13,
                borderRadius: 12,
                border: `1px solid ${C.border}`,
                background: '#fff',
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Guardar borrador
            </button>
            <button
              onClick={publish}
              disabled={submitting}
              style={{
                flex: 2,
                padding: 13,
                borderRadius: 12,
                border: 'none',
                background: C.teal,
                color: '#fff',
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 14,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Publicando…' : 'Publicar ahora'}
            </button>
          </div>
        </div>

        {/* Vista previa */}
        <div style={{ position: 'sticky', top: 20 }}>
          <div
            style={{
              fontFamily: FH,
              fontWeight: 700,
              fontSize: 15,
              marginBottom: 10,
              color: C.ink2,
            }}
          >
            Vista previa
          </div>
          <div
            style={{
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: 130,
                background: STRIPE(
                  (biz.cover_colors as [string, string])?.[0] ?? C.terra,
                  (biz.cover_colors as [string, string])?.[1] ?? '#E28B70'
                ),
              }}
            />
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.terra, marginBottom: 4 }}>
                {badge}
              </div>
              <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15, marginBottom: 3 }}>
                {title.trim() || 'Tu título aparecerá aquí'}
              </div>
              <div style={{ fontSize: 12.5, color: C.ink2 }}>
                {biz.name} · {biz.zone}
              </div>
            </div>
          </div>
          <div
            style={{
              marginTop: 16,
              padding: '12px 16px',
              background: `${C.amber}10`,
              borderRadius: 12,
              fontSize: 13,
              color: C.ink2,
              display: 'flex',
              gap: 8,
            }}
          >
            <Info size={16} color={C.amber} style={{ flexShrink: 0 }} />
            {isPermanent
              ? 'Quedará en tu perfil de forma permanente. No aparece en Inicio ni en el mapa.'
              : 'Aparecerá en Inicio, Ahora y el mapa mientras esté vigente, y luego quedará en tu perfil.'}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── ACTIVIDADES ──────────────────────────────────────────────────────────────
function BizActivities({ posts, biz: _biz }: { posts: PostRow[]; biz: BizRow }) {
  const [filter, setFilter] = useState('Todas')
  const filters = [
    'Todas',
    'En vivo',
    'Publicadas',
    'Programadas',
    'Borradores',
    'Finalizadas',
    'Canceladas',
  ]

  const shown =
    filter === 'Todas'
      ? posts
      : posts.filter((p) => {
          if (filter === 'En vivo') return p.type === 'live' && p.is_active
          if (filter === 'Publicadas') return p.is_active
          if (filter === 'Inactivas') return !p.is_active
          return true
        })

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26 }}>Actividades</div>
          <div style={{ fontSize: 14, color: C.ink2, marginTop: 2 }}>
            {posts.length} publicaciones · {posts.filter((p) => p.is_active).length} activas
          </div>
        </div>
        <button
          onClick={() => {}}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '11px 18px',
            borderRadius: 11,
            border: 'none',
            background: C.teal,
            color: '#fff',
            fontFamily: FB,
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          + Nueva publicación
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          marginBottom: 20,
        }}
      >
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              padding: '8px 14px',
              borderRadius: 99,
              flexShrink: 0,
              border: `1px solid ${filter === f ? C.teal : C.border}`,
              background: filter === f ? C.teal : '#fff',
              color: filter === f ? '#fff' : C.ink,
              fontFamily: FB,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {f}
          </button>
        ))}
      </div>

      <div
        style={{
          background: '#fff',
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          overflow: 'hidden',
        }}
      >
        {/* Encabezado tabla */}
        <div
          className="act-table-hdr"
          style={{
            padding: '12px 20px',
            borderBottom: `1px solid ${C.border}`,
            fontSize: 12,
            fontWeight: 700,
            color: C.ink2,
            textTransform: 'uppercase',
            letterSpacing: '.04em',
          }}
        >
          <span>Publicación</span>
          <span>Estado</span>
          <span style={{ textAlign: 'center' }}>Interés</span>
          <span style={{ textAlign: 'center' }}>Visitas</span>
          <span style={{ textAlign: 'center' }}>Saves</span>
          <span style={{ textAlign: 'right' }}>Acciones</span>
        </div>

        {shown.map((p, i) => {
          const tColor = p.type === 'live' ? C.live : p.type === 'promo' ? C.terra : C.teal
          const tLabel = p.is_active ? (p.type === 'live' ? 'En vivo' : 'Publicado') : 'Inactivo'
          return (
            <div
              key={p.id}
              className="act-table-row"
              style={{
                padding: '14px 20px',
                borderBottom: i < shown.length - 1 ? `1px solid ${C.border}` : 'none',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: STRIPE(C.teal, '#17706f'),
                    flexShrink: 0,
                  }}
                />
                <div
                  style={{
                    fontFamily: FH,
                    fontWeight: 700,
                    fontSize: 14,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.title}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 12,
                  fontWeight: 700,
                  color: tColor,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: 99, background: tColor }} />
                {tLabel}
              </div>
              <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14 }}>
                {p.interested_count}
              </div>
              <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14 }}>
                {p.view_count}
              </div>
              <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14 }}>
                {Math.round(p.interested_count * 0.4)}
              </div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                {(
                  [
                    <Pencil size={14} />,
                    <Copy size={14} />,
                    <MoreVertical size={14} />,
                  ] as React.ReactNode[]
                ).map((ic, j) => (
                  <button
                    key={j}
                    onClick={() => toast.info(['Editar', 'Duplicar', 'Más opciones'][j]!)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      background: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {ic}
                  </button>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── COMUNIDAD ────────────────────────────────────────────────────────────────
function BizCommunity() {
  const [tab, setTab] = useState('Comentarios')
  const tabs = ['Comentarios', 'Fotos de clientes', 'Seguidores']

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26, marginBottom: 6 }}>
        Comunidad
      </div>
      <div style={{ fontSize: 14, color: C.ink2, marginBottom: 20 }}>
        Gestiona la conversación alrededor de tu negocio.
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: 24 }}>
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '12px 18px',
              border: 'none',
              marginBottom: -1,
              borderBottom: `2.5px solid ${tab === t ? C.teal : 'transparent'}`,
              background: 'transparent',
              color: tab === t ? C.teal : C.ink2,
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Comentarios' && (
        <div>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}
          >
            {COMMENTS.map((c, i) => (
              <div
                key={i}
                style={{
                  background: '#fff',
                  border: `1px solid ${C.border}`,
                  borderRadius: 16,
                  padding: 20,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 99,
                      background: c.av,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 14 }}>{c.user}</div>
                    <div style={{ fontSize: 12, color: C.ink2 }}>{c.time}</div>
                  </div>
                </div>
                <div style={{ fontSize: 14, color: C.ink, marginBottom: 14, lineHeight: 1.5 }}>
                  {c.text}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => toast.info('Respondiendo…')}
                    style={{
                      flex: 1,
                      padding: '9px',
                      borderRadius: 9,
                      border: 'none',
                      background: C.teal,
                      color: '#fff',
                      fontFamily: FB,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Responder
                  </button>
                  <button
                    onClick={() => toast.info('Reportado')}
                    style={{
                      padding: '9px 12px',
                      borderRadius: 9,
                      border: `1px solid ${C.border}`,
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    🚩
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              padding: '12px 16px',
              background: `${C.amber}10`,
              borderRadius: 12,
              fontSize: 13,
              color: C.ink2,
              display: 'flex',
              gap: 8,
            }}
          >
            <Info size={16} color={C.amber} style={{ flexShrink: 0 }} />
            No puedes eliminar críticas legítimas. Si un comentario viola las normas, solicita
            revisión a moderación.
          </div>
        </div>
      )}

      {tab === 'Fotos de clientes' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                style={{
                  aspectRatio: '1',
                  borderRadius: 14,
                  background: STRIPE(C.teal, '#17706f'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,.6)',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                ✓ Verificado
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'Seguidores' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {['Andrea M.', 'Carlos R.', 'Valeria P.', 'Diego T.', 'Sofía R.', 'Luis A.'].map(
            (n, i) => (
              <div
                key={n}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 14,
                  background: '#fff',
                  border: `1px solid ${C.border}`,
                  borderRadius: 13,
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 99,
                    background: [C.teal, C.terra, C.amber][i % 3],
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 14 }}>{n}</div>
                  <div style={{ fontSize: 12, color: C.ink2 }}>
                    Siguiendo desde hace {i + 1} días
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}

// ─── PERFIL DEL NEGOCIO ───────────────────────────────────────────────────────
function BizProfile({ biz }: { biz: BizRow }) {
  const field = (label: string, placeholder: string, defaultVal?: string) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.ink2, marginBottom: 6 }}>{label}</div>
      <input
        defaultValue={defaultVal}
        placeholder={placeholder}
        style={{
          width: '100%',
          border: `1px solid ${C.border}`,
          borderRadius: 11,
          padding: '11px 14px',
          fontFamily: FB,
          fontSize: 14,
          outline: 'none',
        }}
      />
    </div>
  )

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26, marginBottom: 20 }}>
        Perfil del negocio
      </div>
      <div className="biz-two-col">
        <div
          style={{
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 18,
            padding: 24,
          }}
        >
          {/* Portada + logo */}
          <div
            style={{
              height: 140,
              borderRadius: 14,
              background: STRIPE(C.teal, '#17706f'),
              position: 'relative',
              marginBottom: 44,
            }}
          >
            <button
              onClick={() => toast.info('Cambiar portada')}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(255,255,255,.9)',
                border: 'none',
                borderRadius: 9,
                padding: '7px 12px',
                fontFamily: FB,
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              📷 Portada
            </button>
            <div
              style={{
                position: 'absolute',
                bottom: -32,
                left: 20,
                width: 76,
                height: 76,
                borderRadius: 18,
                background: STRIPE('#17706f', C.teal),
                border: '4px solid #fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 30,
              }}
            >
              🌅
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {field('Nombre', '', biz.name)}
            {field('Categoría', '', biz.category)}
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.ink2, marginBottom: 6 }}>
              Descripción
            </div>
            <textarea
              defaultValue={biz.description ?? ''}
              rows={3}
              style={{
                width: '100%',
                border: `1px solid ${C.border}`,
                borderRadius: 11,
                padding: '11px 14px',
                fontFamily: FB,
                fontSize: 14,
                outline: 'none',
                resize: 'none',
              }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {field('Zona', '', biz.zone)}
            {field('Dirección', '', biz.address ?? '')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {field('WhatsApp', '', biz.whatsapp ?? '')}
            {field('Instagram', '', biz.instagram ?? '')}
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.ink2, marginBottom: 8 }}>
              Servicios disponibles
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {['Estacionamiento', 'Pet friendly', 'Accesible', 'Terraza', 'Wi-Fi', 'Valet'].map(
                (s) => (
                  <span
                    key={s}
                    style={{
                      background: `${C.teal}12`,
                      color: C.teal,
                      borderRadius: 99,
                      padding: '7px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {s}
                  </span>
                )
              )}
            </div>
          </div>

          <button
            onClick={() => toast.success('Perfil actualizado ✓')}
            style={{
              width: '100%',
              padding: 13,
              borderRadius: 12,
              border: 'none',
              background: C.teal,
              color: '#fff',
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Guardar cambios
          </button>
        </div>

        {/* Estado del perfil */}
        <div style={{ position: 'sticky', top: 20 }}>
          <div
            style={{
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: 18,
              marginBottom: 14,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: STRIPE(C.teal, '#17706f'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                }}
              >
                🌅
              </div>
              <div>
                <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15 }}>{biz.name}</div>
                <div
                  style={{
                    fontSize: 12,
                    color: C.teal,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 2,
                  }}
                >
                  <BadgeCheck size={12} /> Verificado
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 13,
                color: C.ink2,
                padding: '10px 0',
                borderTop: `1px solid ${C.border}`,
              }}
            >
              <span>Seguidores</span>
              <span style={{ fontWeight: 700, color: C.ink }}>4,820</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 13,
                color: C.ink2,
                padding: '10px 0',
                borderTop: `1px solid ${C.border}`,
              }}
            >
              <span>Plan actual</span>
              <span style={{ fontWeight: 700, color: C.teal }}>Profesional</span>
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: 18,
            }}
          >
            <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
              Completud del perfil
            </div>
            <div style={{ height: 8, borderRadius: 99, background: C.grayBg, marginBottom: 8 }}>
              <div style={{ height: '100%', width: '85%', borderRadius: 99, background: C.teal }} />
            </div>
            <div style={{ fontSize: 13, color: C.ink2 }}>
              85% completo — Agrega horarios para llegar al 100%
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── SUSCRIPCIÓN ──────────────────────────────────────────────────────────────
function BizSubscription({ biz }: { biz: BizRow }) {
  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26, marginBottom: 6 }}>
        Suscripción
      </div>
      <div style={{ fontSize: 15, color: C.ink2, marginBottom: 28 }}>
        Elige el plan que mejor se adapte a tu negocio.
      </div>

      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, marginBottom: 24 }}
      >
        {PLANS.map((p) => {
          const current = p.key === biz.plan
          return (
            <div
              key={p.name}
              style={{
                background: current ? C.teal : '#fff',
                color: current ? '#fff' : C.ink,
                border: `1px solid ${current ? C.teal : C.border}`,
                borderRadius: 20,
                padding: 24,
                position: 'relative',
              }}
            >
              {current && (
                <div
                  style={{
                    position: 'absolute',
                    top: 18,
                    right: 18,
                    background: C.amber,
                    color: '#fff',
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: '4px 10px',
                    borderRadius: 7,
                  }}
                >
                  ACTUAL
                </div>
              )}
              <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 20, marginBottom: 4 }}>
                {p.name}
              </div>
              <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 28, marginBottom: 18 }}>
                {p.price}
              </div>
              {p.features.map((f) => (
                <div
                  key={f}
                  style={{
                    display: 'flex',
                    gap: 9,
                    alignItems: 'center',
                    marginBottom: 10,
                    fontSize: 14,
                  }}
                >
                  <span style={{ color: current ? C.amber : C.success }}>✓</span> {f}
                </div>
              ))}
              <button
                onClick={() => toast.info(current ? 'Ya estás en este plan' : 'Plan seleccionado')}
                style={{
                  width: '100%',
                  marginTop: 16,
                  padding: 13,
                  borderRadius: 12,
                  border: 'none',
                  background: current ? 'rgba(255,255,255,.2)' : p.color,
                  color: '#fff',
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                {current ? 'Tu plan actual' : 'Seleccionar plan'}
              </button>
            </div>
          )
        })}
      </div>

      <div
        style={{
          background: `${C.amber}10`,
          border: `1px solid ${C.amber}30`,
          borderRadius: 14,
          padding: '16px 20px',
          display: 'flex',
          gap: 12,
        }}
      >
        <Info size={18} color={C.amber} style={{ flexShrink: 0 }} />
        <div>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
            Modo demo
          </div>
          <div style={{ fontSize: 13.5, color: C.ink2, lineHeight: 1.5 }}>
            Los pagos y cambios de plan son simulados. En producción se conectará con Stripe.
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Entrada del dashboard ────────────────────────────────────────────────────
// ── Router sin nested Routes — usa useLocation para decidir qué renderizar ──
function BizContent({ biz, posts, reload }: { biz: BizRow; posts: PostRow[]; reload: () => void }) {
  const { pathname } = useLocation()
  const p = pathname.replace('/dashboard', '').replace(/^\//, '')

  if (!p || p === '') return <BizHome biz={biz} posts={posts} />
  if (p === 'create') return <BizCreate biz={biz} onCreated={reload} />
  if (p === 'activities') return <BizActivities posts={posts} biz={biz} />
  if (p === 'community') return <BizCommunity />
  if (p === 'profile') return <BizProfile biz={biz} />
  if (p === 'subscription') return <BizSubscription biz={biz} />
  return <BizHome biz={biz} posts={posts} />
}

export default function BizDashboard() {
  const { biz, posts, loading, reload } = useBizData()

  if (loading) {
    return (
      <div
        style={{
          height: '100vh',
          background: '#F1F3F2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 99,
            border: '3px solid #0F5C5C',
            borderTopColor: 'transparent',
            animation: 'pxSpin 1s linear infinite',
          }}
        />
        <span style={{ fontSize: 14, color: '#6D7471', fontFamily: "'Inter',sans-serif" }}>
          Cargando tu negocio…
        </span>
      </div>
    )
  }

  if (!biz) {
    return (
      <div
        style={{
          height: '100vh',
          background: '#F1F3F2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <span style={{ fontSize: 48 }}>🏪</span>
        <span
          style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 18 }}
        >
          No tienes un negocio registrado aún
        </span>
        <span style={{ fontSize: 14, color: '#6D7471' }}>
          Solicita el acceso desde el panel de administración.
        </span>
      </div>
    )
  }

  return (
    <BizShell biz={biz}>
      <BizContent biz={biz} posts={posts} reload={reload} />
    </BizShell>
  )
}
